import clsx from 'clsx'
import type { HTMLAttributes } from 'react'

export interface ProgressBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'role'> {
  /** 0-100 inclusive. If omitted the bar shows an indeterminate state. */
  value?: number
  /** Defaults to "Progress". */
  label?: string
  size?: 'sm' | 'md'
  tone?: 'accent' | 'green' | 'amber' | 'red'
}

const sizeClass: Record<NonNullable<ProgressBarProps['size']>, string> = {
  sm: 'h-1.5',
  md: 'h-2.5',
}

const toneClass: Record<NonNullable<ProgressBarProps['tone']>, string> = {
  accent: 'bg-[var(--accent)]',
  green: 'bg-[var(--green)]',
  amber: 'bg-[var(--amber)]',
  red: 'bg-[var(--red)]',
}

export function ProgressBar({
  value,
  label = 'Progress',
  size = 'md',
  tone = 'accent',
  className,
  ...rest
}: ProgressBarProps) {
  const indeterminate = typeof value !== 'number'
  const clamped = indeterminate ? 0 : Math.min(100, Math.max(0, value))

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={indeterminate ? undefined : Math.round(clamped)}
      aria-busy={indeterminate || undefined}
      className={clsx(
        'w-full overflow-hidden rounded-[var(--radius-sm)] bg-[var(--bg-3)]',
        sizeClass[size],
        className,
      )}
      {...rest}
    >
      <div
        className={clsx(
          'h-full transition-[width] duration-200 ease-out',
          toneClass[tone],
          indeterminate && 'animate-pulse',
        )}
        style={{ width: indeterminate ? '40%' : `${clamped}%` }}
      />
    </div>
  )
}
